import { ValidationResult, OwnerState } from "../types/owner-edit";
import { VALIDATION_RULES, ERROR_MESSAGES } from "../constants/owner-edit";
import { enqueueSnackbar, closeSnackbar } from "notistack";

export const useOwnerValidation = () => {
  const validateUrl = (url: string): boolean => {
    if (!url) return true; // Allow empty values
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const validateSocialUrl = (url: string, platformName: string): boolean => {
    const isValid = validateUrl(url);
    if (!isValid && url) {
      enqueueSnackbar(`${platformName} ${ERROR_MESSAGES.INVALID_URL}`, {
        SnackbarProps: {
          onClick: () => {
            closeSnackbar();
          },
        },
        variant: "error",
        autoHideDuration: 1000,
      });
    }
    return isValid;
  };

  const validateUsername = (username: string): ValidationResult => {
    const errors: string[] = [];

    if (
      !username ||
      username.trim().length < VALIDATION_RULES.USERNAME_MIN_LENGTH
    ) {
      errors.push("Username is required");
    }

    if (username.length > VALIDATION_RULES.USERNAME_MAX_LENGTH) {
      errors.push(
        `Username must be less than ${VALIDATION_RULES.USERNAME_MAX_LENGTH} characters`
      );
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  };

  const validateInfo = (info: string): ValidationResult => {
    const errors: string[] = [];

    if (info.length > VALIDATION_RULES.INFO_MAX_LENGTH) {
      errors.push(
        `Info must be less than ${VALIDATION_RULES.INFO_MAX_LENGTH} characters`
      );
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  };

  const validateOwnerData = (ownerData: OwnerState): ValidationResult => {
    const errors: string[] = [];

    // Validate username
    const usernameValidation = validateUsername(ownerData.username);
    if (!usernameValidation.isValid) {
      errors.push(...usernameValidation.errors);
    }

    // Validate info
    const infoValidation = validateInfo(ownerData.info);
    if (!infoValidation.isValid) {
      errors.push(...infoValidation.errors);
    }

    // Validate social links
    const invalidSocials = ownerData.socials.filter(
      social => !validateUrl(social.link) && social.link !== ""
    );

    if (invalidSocials.length > 0) {
      errors.push("One or more social links are invalid");
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  };

  const validateBeforeSave = (ownerData: OwnerState): boolean => {
    const validation = validateOwnerData(ownerData);

    if (!validation.isValid) {
      validation.errors.forEach(error => {
        enqueueSnackbar(error, {
          SnackbarProps: {
            onClick: () => {
              closeSnackbar();
            },
          },
          variant: "error",
          autoHideDuration: 2000,
        });
      });
      return false;
    }

    return true;
  };

  return {
    validateUrl,
    validateSocialUrl,
    validateUsername,
    validateInfo,
    validateOwnerData,
    validateBeforeSave,
  };
};
